import {Record, List, Iterable} from 'immutable';
import {TriggerActionRecipientType} from 'records/TriggerActionRecipient';

export const TriggerActionType = Object.freeze({
  EMAIL: 'email_action',
  SLACK: 'slack_action',
  TREND: 'trend_action',
  FOLDER: 'folder_action',
  // DASHBOARD is only used in default topics triggers and should not be visible on the frontend.

  getActionConfiguration: (type, props) => {
    switch (type) {
      case TriggerActionType.EMAIL:
        return new TriggerEmailAction(props);
      case TriggerActionType.FOLDER:
        return new TriggerFolderAction(props);
      case TriggerActionType.TREND:
        return new TriggerTrendAction(props);
      case TriggerActionType.SLACK:
        return new TriggerSlackAction(props);
      default:
        return new TriggerFolderAction(props);
    }
  },

  getEntityType: type => {
    switch (type) {
      case TriggerActionType.EMAIL:
        return TriggerActionEntityType.EMAIL;
      case TriggerActionType.FOLDER:
        return TriggerActionEntityType.FOLDER;
      case TriggerActionType.SLACK:
        return TriggerActionEntityType.SLACK;
      default:
        return TriggerActionEntityType.FOLDER;
    }
  },
});

export const TriggerActionEntityType = Object.freeze({
  EMAIL: 'EMAIL',
  SLACK: 'SLACK',
  TREND: 'TREND',
  FOLDER: 'FOLDER',
  // DASHBOARD is only used in default topics triggers and should not be visible on the frontend.
});

export default class TriggerRuleAction extends Record({
  id: 0,
  active: true,
  ruleId: null,
  trendId: null,
  type: null,
  configuration: null,
}) {
  constructor(props = {}) {
    super({
      ...props,
      configuration: props.configuration
        ? Iterable.isIterable(props.configuration)
          ? props.configuration
          : TriggerActionType.getActionConfiguration(props.configuration.type, props.configuration)
        : null,
    });
  }

  isValid() {
    return !this.active || this.configuration.isValid();
  }

  toEntity() {
    return this.toJS();
  }
}

export class TriggerFolderAction extends Record({
  type: TriggerActionType.FOLDER,
  notificationTo: List(),
}) {
  constructor(props = {}) {
    const notifications = Iterable.isIterable(props.notificationTo)
      ? props.notificationTo
      : List(props.notificationTo);
    super({
      ...props,
      notificationTo: notifications.map(item =>
        TriggerActionRecipientType.getTriggerActionRecipient(item.type, item)
      ),
    });
  }

  isValid() {
    return validateRecipients(this.notificationTo);
  }
}

export class TriggerEmailAction extends Record({
  type: TriggerActionType.EMAIL,
  subject: '',
  template: '',
  emailTo: List(),
}) {
  constructor(props = {}) {
    const emails = Iterable.isIterable(props.emailTo) ? props.emailTo : List(props.emailTo);
    super({
      ...props,
      emailTo: emails.map(item =>
        TriggerActionRecipientType.getTriggerActionRecipient(item.type, item)
      ),
    });
  }

  isValid() {
    return validateRecipients(this.emailTo);
  }
}

export class TriggerTrendAction extends Record({
  type: TriggerActionType.TREND,
  trendType: null,
}) {
  isValid() {
    return true;
  }
}

export class TriggerSlackAction extends Record({
  type: TriggerActionType.SLACK,
  notificationTo: List(),
}) {
  isValid() {
    return validateRecipients(this.notificationTo);
  }
}

function validateRecipients(recipientList) {
  return recipientList.every(
    i =>
      (i.type !== TriggerActionRecipientType.TEAM && i.type !== TriggerActionRecipientType.USER) ||
      (i.type === TriggerActionRecipientType.TEAM && !!i.teamId) ||
      (i.type === TriggerActionRecipientType.USER && !!i.userId)
  );
}
